// Typed API client. All browser traffic goes through /api — same origin,
// served by the Node service behind Caddy (Hetzner, seit 2026-09-13; vorher
// Pages-Function-Proxy → Cloudflare Worker). Nie eine andere Origin ansprechen.

import type {
  ToolConfig,
  UploadVersion,
  BrandPresets,
  TtsParamsV2,
  TtsParamsV4,
  TtsModel,
  STSParams,
} from '@shared/types';
import { applyVoiceIdsMigration } from './voiceMigration';
import { TTS_MODEL_IDS } from './modelLock';
import { fetchVideo } from './videoSource';
import { generationRetry, generationRetryDelayMs, generationTimeouts, isProxyOutage, isRetriableGenerationError, isRetriableUploadError } from './apiRetry';

const BASE = '/api';

// Session password singleton. Set once by useToolConfig after a password-
// protected config loads successfully, then auto-attached as X-Survey-Password
// to every /api request (raw() below + the XHR upload path). The server now
// enforces the password on /tts, /sts and /upload — not just /config — so
// without this a leaked tool_id could drive ElevenLabs/uploads past the
// password. Undefined for tools with no password → header never sent.
let sessionPassword: string | undefined;

/** Called by useToolConfig once the (optional) password has validated against
 *  /config. Pass undefined to clear (tool has no password). */
export function setSessionPassword(pw: string | undefined): void {
  sessionPassword = pw || undefined;
}

let sessionLang: string | undefined;
/** Multilingual: von useToolConfig gesetzt, danach als X-Survey-Lang an alle
 *  Talent-Calls angehängt (der Dienst bindet das PW strikt an die Sprache).
 *  Undefined für Standard-Tools → Header wird nie gesendet. */
export function setSessionLang(lang: string | undefined): void { sessionLang = lang; }

/** Projekt, zu dem Passwort und Sprache oben gehören. Die Singletons
 *  überleben SPA-Navigation — ohne diese Bindung schickte getConfig(B) das
 *  Passwort von A mit (ein gezählter Fehlversuch bei B, Integrations-Review
 *  2026-09-24). */
let sessionToolId: string | undefined;
function bindTalentSession(toolId: string): void {
  if (sessionToolId === toolId) return;
  sessionToolId = toolId;
  sessionPassword = undefined;
  sessionLang = undefined;
}

export class ApiError extends Error {
  /** `Retry-After` der Antwort (Sekunden), wenn der Dienst eins nennt — 429 der ElevenLabs-Warteschlange. */
  retryAfter: string | null = null;
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Von useToolConfig registriert: ein 401 auf einer Talent-Route, obwohl ein
 *  validiertes Passwort mitgeschickt wurde, heißt „der Admin hat das Passwort
 *  mitten in der Session geändert". Statt „Generation failed" zeigt die App
 *  dann wieder die Passwortabfrage (Audit, Low). `/config` selbst ist
 *  ausgenommen — dort ist 401 der normale Weg zur Abfrage. */
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

/** 401 auf einer Talent-Route mit gesetztem Passwort → Handler (Passwort-
 *  Prompt). Gemeinsam für raw() und die XHR-Pfade (Bundle-Upload, große Dateien),
 *  die an raw() vorbeilaufen (Integrations-Review 2026-09-24). */
function noteUnauthorized(status: number, path: string): void {
  if (status === 401 && sessionPassword && !path.startsWith('/config/')) {
    try { unauthorizedHandler?.(); } catch { /* ignore */ }
  }
}

async function raw(
  path: string,
  init: RequestInit = {}
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (sessionPassword && !headers.has('X-Survey-Password')) {
    headers.set('X-Survey-Password', sessionPassword);
  }
  if (sessionLang && !headers.has('X-Survey-Lang')) headers.set('X-Survey-Lang', sessionLang);
  const res = await fetch(BASE + path, { ...init, headers });
  // AbortError surfaces as a `DOMException` thrown by `fetch` itself, not as a
  // non-OK response — it never reaches this branch. Callers passing
  // `init.signal` should `try`/`catch` for `err.name === 'AbortError'`.
  noteUnauthorized(res.status, path);
  if (!res.ok) {
    let body: unknown = undefined;
    const ct = res.headers.get('content-type') || '';
    try {
      body = ct.includes('application/json') ? await res.json() : await res.text();
    } catch {
      /* ignore */
    }
    const msg =
      (body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : null) ||
      `${res.status} ${res.statusText}`;
    const err = new ApiError(res.status, msg, body);
    err.retryAfter = res.headers.get('retry-after');
    throw err;
  }
  return res;
}

/**
 * TTS/STS-Request mit Wiederholung und Client-Frist (Audit 2026-09-24, M13).
 * 429 (ElevenLabs-Parallelität / Warteschlange des Dienstes) und 503 werden
 * bis zu `generationRetry.retries`-mal mit Backoff wiederholt, Retry-After des
 * Servers gewinnt (max. 20 s). Jeder Versuch hat eine eigene Frist; läuft
 * sie ab, endet der Aufruf mit einem Fehler namens `TimeoutError` (ohne
 * weitere Wiederholung — der Spinner drehte sonst minutenlang). Der Abbruch
 * des Aufrufers (`signal`) gewinnt immer und beendet auch die Wartepause.
 */
async function generationRequest(path: string, body: unknown, opts: { signal?: AbortSignal; timeoutMs: number }): Promise<ArrayBuffer> {
  for (let attempt = 0; ; attempt++) {
    opts.signal?.throwIfAborted();
    const controller = new AbortController();
    let timedOut = false;
    const onOuterAbort = () => controller.abort();
    opts.signal?.addEventListener('abort', onOuterAbort, { once: true });
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, opts.timeoutMs);
    try {
      const res = await raw(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      return await res.arrayBuffer();
    } catch (e) {
      if (timedOut && !opts.signal?.aborted) {
        throw Object.assign(new Error(`No response within ${Math.round(opts.timeoutMs / 1000)} s`), { name: 'TimeoutError' });
      }
      if (opts.signal?.aborted || attempt >= generationRetry.retries || !isRetriableGenerationError(e)) throw e;
      const delay = generationRetryDelayMs(attempt, (e as ApiError).retryAfter);
      await new Promise<void>((resolve) => {
        const t = setTimeout(resolve, delay);
        opts.signal?.addEventListener('abort', () => { clearTimeout(t); resolve(); }, { once: true });
      });
    } finally {
      clearTimeout(timer);
      opts.signal?.removeEventListener('abort', onOuterAbort);
    }
  }
}

async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await raw(path, init);
  return (await res.json()) as T;
}

/**
 * Idempotenter GET (Config, Session-Stand) mit Wiederholung NUR bei einem
 * Proxy-Ausfall (`isProxyOutage`: Caddy-502/503/504 während des Deploy-
 * Restarts, Integrations-Review 2026-09-24). Pausen und Kappung wie bei der
 * Generierung; alle anderen Fehler (401, 404, Netz) gehen sofort durch.
 */
async function idempotentJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await json<T>(path, init);
    } catch (e) {
      if (init.signal?.aborted || attempt >= generationRetry.retries || !isProxyOutage(e)) throw e;
      await new Promise<void>((r) => setTimeout(r, generationRetryDelayMs(attempt, (e as ApiError).retryAfter)));
    }
  }
}

// --------------------------------------------------------------------------
// Public (voice-talent)
// --------------------------------------------------------------------------

export async function getConfig(
  toolId: string,
  password?: string,
  lang?: string
): Promise<ToolConfig> {
  // Anderes Projekt als bisher → Passwort/Sprache des alten verwerfen, bevor
  // raw() sie anhängt.
  bindTalentSession(toolId);
  const headers: Record<string, string> = {};
  if (password) headers['X-Survey-Password'] = password;
  if (lang) headers['X-Survey-Lang'] = lang;
  // The server returns { success, config }. `cache: 'no-store'` is CRITICAL: the
  // 200 response carries no Cache-Control/Vary, so the browser would otherwise
  // heuristically cache it keyed by URL (the password header is not part of the
  // key). A reload then serves the cached 200 WITHOUT re-prompting for the
  // password → setSessionPassword never runs → /tts/sts/upload go out without
  // X-Survey-Password → 401. Forcing a network round-trip re-validates the
  // password every load and keeps the session-password singleton in sync.
  const { config } = await idempotentJson<{ success: true; config: ToolConfig }>(
    `/config/${toolId}`,
    { headers, cache: 'no-store' }
  );
  if (config.assets) {
    config.assets = applyVoiceIdsMigration(config.assets);
  }
  return config;
}

export interface TtsRequest {
  toolId: string;
  text: string;
  voiceId: string;
  model: TtsModel;
  v2?: TtsParamsV2;
  v4?: TtsParamsV4;
  /** Asset, zu dem der Block gehört. Der Dienst setzt damit den Asset-
   *  Model-Lock (inkl. Sprach-Override) serverseitig durch (Audit M2).
   *  Ohne — oder bei Custom-Assets — prüft er nur den Voice-Lock. */
  assetId?: string;
  /** Optional cancellation signal. When the caller aborts this signal the
   *  underlying `fetch` rejects with `AbortError` and no response is parsed.
   *  Wired into TTSView's per-asset cancel-on-switch path so the server
   *  request stops mid-flight when the user navigates to another asset. */
  signal?: AbortSignal;
}

/** Returns an MP3 ArrayBuffer. Translates the React-app's typed shape into the
 *  legacy wire format (`tool_id`, `voice_id`, `model_id`, flat voice
 *  settings). Eleven v4 only knows stability + similarity — style, speed and
 *  speaker boost are not sent (the server drops them for v4 anyway). */
export async function generateTts(req: TtsRequest): Promise<ArrayBuffer> {
  const base = {
    tool_id: req.toolId,
    voice_id: req.voiceId,
    text: req.text,
    model_id: TTS_MODEL_IDS[req.model],
    ...(req.assetId ? { asset_id: req.assetId } : {}),
  };
  const v2 = req.v2;
  const body = req.model === 'v4'
    ? {
      ...base,
      stability: req.v4?.stability ?? 0.5,
      similarity_boost: req.v4?.similarity ?? 0.75,
    }
    : {
      ...base,
      stability: v2?.stability ?? 0.5,
      similarity_boost: v2?.similarity ?? 0.75,
      style: v2?.style ?? 0,
      speed: 1.0,
      use_speaker_boost: v2?.speakerBoost ?? true,
    };
  return generationRequest('/tts', body, { signal: req.signal, timeoutMs: generationTimeouts.ttsMs });
}

export interface StsRequest {
  toolId: string;
  audio: Blob;
  audioMime: string;
  voiceId: string;
  params: STSParams;
  /** Optional cancellation signal — symmetric with `TtsRequest.signal`. */
  signal?: AbortSignal;
}

/** Returns an MP3 ArrayBuffer.
 *
 *  Wire format per `POST /sts` in `server/routes/talent.ts` — JSON body with
 *  base64-encoded audio and flat voice settings. The server reassembles the multipart payload for
 *  the ElevenLabs STS endpoint server-side. Sending as multipart from the
 *  browser would require streaming the file through the server as-is; JSON
 *  base64 keeps the API symmetric with `/tts`, which is already proven. */
export async function generateSts(req: StsRequest): Promise<ArrayBuffer> {
  const arrayBuf = await req.audio.arrayBuffer();
  const audioBase64 = bufferToBase64(arrayBuf);
  const body = {
    tool_id: req.toolId,
    voice_id: req.voiceId,
    audioBase64,
    audioMimeType: req.audioMime,
    stability: req.params.stability,
    similarity_boost: req.params.similarity,
    style: req.params.style,
    use_speaker_boost: req.params.speakerBoost,
    remove_background_noise: req.params.removeNoise,
  };
  return generationRequest('/sts', body, { signal: req.signal, timeoutMs: generationTimeouts.stsMs });
}

/** Encode an ArrayBuffer to base64 in chunks so large payloads don't blow
 *  the argument limit of `String.fromCharCode(...array)`. */
function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  const CHUNK = 0x8000; // 32KB — safely under JS engine argument limits
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, Math.min(i + CHUNK, bytes.length));
    binary += String.fromCharCode.apply(null, slice as unknown as number[]);
  }
  return btoa(binary);
}

export interface UploadFilePayload {
  filename: string;
  blob: Blob;
  isFinal?: boolean;
}

export interface UploadResult {
  version: number;
  files: string[];
  /** `pending`, solange angekündigte große Dateien (`largeFiles`) noch fehlen (Audit M15). */
  status?: 'pending' | 'complete';
  /** Marke der Reservierung; der Cancel schickt sie mit (Integrations-Review 2026-09-24). */
  uploadId?: string;
  /** Asset-Ordner, in dem der Server die Version abgelegt hat — bei einem
   *  Asset mit Versionen der Ordner der bisherigen (Integrations-Review 2026-09-24). */
  folder?: string;
}

/** Optional KV metadata that travels with a versioned upload. The server
 *  records `note`/`userName`/`assetId` on the session KV entry for the version
 *  written by the multipart request (`POST /upload/:toolId` in `server/routes/talent.ts`).
 *  `assetId` keys the per-asset versions map, mirroring the legacy JSON path
 *  (`session.versions[assetId]`). `noteFilename` controls the WebDAV note-file
 *  name (defaults to `note.txt`). */
export interface UploadAssetMeta {
  note?: string | null;
  noteFilename?: string;
  userName?: string;
  assetId?: string;
  /** Sub-mode marker for free/custom assets. The server persists this on the
   *  version record (`UploadVersion.mode`) so VersionsBar can render a TTS/STS
   *  chip on custom-asset pills. Only set when the asset uses freeMode —
   *  predefined TTS-only / STS-only uploads pass `undefined` and the server
   *  stores no `mode` field (legacy-record parity). */
  freeMode?: 'tts' | 'sts';
  /** Custom-Asset: Titel für den Session-Stand, damit `/stream` den
   *  Ordnernamen (`N - Custom - Titel`) bilden kann. Vorgegebene Assets
   *  lassen das Feld weg (der Dienst kennt ihren Titel aus der Config). */
  customTitle?: string;
  /** Dateien, die NACH dem Bundle einzeln über `/upload-file` folgen (Video,
   *  lange WAVs). Der Dienst trägt sie als erwartet in den Versions-Record
   *  ein und lässt die Version `pending`, bis die letzte davon in der Cloud
   *  liegt (Audit M15). */
  largeFiles?: string[];
}

/** Optional progress callback for `uploadAsset`. `loaded`/`total` are bytes
 *  transferred; `total` is 0 when the browser can't determine it. The
 *  callback fires during the multipart POST upload phase (before the
 *  server's response is parsed). Implemented via XHR under the hood since
 *  `fetch` doesn't expose request-body progress. */
export type UploadAssetProgress = (loaded: number, total: number) => void;

/** Whole-version-in-one-request upload. The server's `/upload/:toolId` handler
 *  parses this multipart FormData payload and:
 *    1. reserves the next version synchronously (record `pending`,
 *       `server/uploadVersions.ts`),
 *    2. MKCOL's `{cloudPath}/{sessionFolder}/{assetFolderName}/Version {n}/`
 *       (or the folder the asset's earlier versions live in),
 *    3. PUTs every `file{i}` blob into that folder,
 *    4. PUTs `noteFilename` (containing `note`) when both are set,
 *    5. commits the record (`complete` once all expected files are there).
 *  Parsed field names: `POST /upload/:toolId` in `server/routes/talent.ts`.
 *
 *  When `onUploadProgress` is provided we use XHR so the caller gets bytes-
 *  sent updates; the multipart wire format is identical to the fetch path. */
export async function uploadAsset(
  toolId: string,
  sessionFolder: string,
  assetIdx: number,
  assetFolderName: string,
  files: UploadFilePayload[],
  duration: number,
  meta?: UploadAssetMeta,
  onUploadProgress?: UploadAssetProgress,
  signal?: AbortSignal,
): Promise<UploadResult> {
  const fd = new FormData();
  fd.append('sessionFolder', sessionFolder);
  fd.append('assetIdx', String(assetIdx));
  fd.append('assetFolderName', assetFolderName);
  fd.append('duration', String(duration));
  if (meta?.assetId) fd.append('assetId', meta.assetId);
  if (meta?.note != null) fd.append('note', meta.note);
  if (meta?.noteFilename) fd.append('noteFilename', meta.noteFilename);
  if (meta?.userName) fd.append('userName', meta.userName);
  if (meta?.freeMode) fd.append('freeMode', meta.freeMode);
  if (meta?.customTitle) fd.append('customTitle', meta.customTitle);
  if (meta?.largeFiles?.length) fd.append('largeFiles', JSON.stringify(meta.largeFiles));
  files.forEach((f, i) => {
    fd.append(`file${i}`, f.blob, f.filename);
    if (f.isFinal) fd.append(`final${i}`, '1');
  });

  if (!onUploadProgress) {
    return json<UploadResult>(`/upload/${toolId}`, {
      method: 'POST',
      body: fd,
      signal,
    });
  }

  return new Promise<UploadResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}/upload/${toolId}`);
    xhr.responseType = 'json';
    // XHR bypasses raw() — attach the session password by hand so the server's
    // /upload password check passes for password-protected tools.
    if (sessionPassword) xhr.setRequestHeader('X-Survey-Password', sessionPassword);
    if (sessionLang) xhr.setRequestHeader('X-Survey-Lang', sessionLang);
    // Wire AbortSignal — when it fires, abort the XHR (load/error/abort
    // handlers below will reject the promise via the abort listener).
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
      } else {
        signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }
    }
    xhr.upload.addEventListener('progress', (e) => {
      onUploadProgress(e.loaded, e.lengthComputable ? e.total : 0);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const body = xhr.response as UploadResult | null;
        if (!body) {
          reject(new ApiError(xhr.status, 'Empty upload response'));
          return;
        }
        resolve(body);
      } else {
        noteUnauthorized(xhr.status, `/upload/${toolId}`);
        const body = xhr.response as { message?: string } | null;
        const msg = body?.message || `${xhr.status} ${xhr.statusText}`;
        reject(new ApiError(xhr.status, msg, body));
      }
    });
    xhr.addEventListener('error', () => {
      reject(new ApiError(0, 'Network error during upload'));
    });
    xhr.addEventListener('abort', () => {
      const err = new ApiError(0, 'Upload aborted');
      // Mark as AbortError so caller can distinguish user-cancel from
      // genuine network failure.
      (err as unknown as { name: string }).name = 'AbortError';
      reject(err);
    });
    xhr.send(fd);
  });
}

/** Progress of a large-file upload: bytes sent so far of the whole file. */
export type UploadLargeFileProgress = (sentBytes: number, totalBytes: number) => void;

export interface UploadLargeFileArgs {
  toolId: string;
  version: number;
  sessionFolder: string;
  assetFolderName: string;
  assetId: string;
  filename: string;
  blob: Blob;
  onProgress?: UploadLargeFileProgress;
  /** Aborts the in-flight XHR. Caller (UploadModal via pipeline) uses this
   *  for the X-button-confirm-cancel flow. */
  signal?: AbortSignal;
}

/** Threshold above which uploadPipeline sends a file on its own via
 *  `/upload-file` instead of bundling it into the multipart `/upload`
 *  payload (whose Caddy body cap is 100 MB). */
export const LARGE_FILE_THRESHOLD = 50 * 1024 * 1024;

/** Upload one large file (video, long WAV) as a SINGLE streamed PUT to
 *  `/upload-file/:toolId/:version`. The server pipes the body straight to
 *  WebDAV; Caddy allows 2 GB on this path (tro-hetzner Caddyfile,
 *  voicestudio.tro.de, 2026-10-02).
 *
 *  Replaces the 80-MB-chunk upload via Seafile upload-links (2026-05 →
 *  2026-10-02): Seafile committed the file as soon as the chunk holding the
 *  last byte range arrived, without checking the other ranges — parallel
 *  chunks produced videos with zero-filled holes. With no Cloudflare body
 *  limit left there is no reason to split at all (docs/decisions.md).
 *
 *  The file must have been announced in the bundle's `largeFiles` (the
 *  server only accepts names its version record expects). XHR is used so
 *  the caller gets byte-level progress. Transient failures (network, 5xx,
 *  408 from a slow uplink) are retried a few times with backoff — the PUT
 *  is idempotent; user aborts and other 4xx are not retried. */
export async function uploadLargeFile(args: UploadLargeFileArgs): Promise<{ filename: string; status: string }> {
  const sendOnce = (): Promise<{ filename: string; status: string }> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', `${BASE}/upload-file/${args.toolId}/${args.version}`);
      xhr.responseType = 'json';
      xhr.setRequestHeader('Content-Type', args.blob.type || 'application/octet-stream');
      xhr.setRequestHeader('X-Filename', args.filename);
      xhr.setRequestHeader('X-Session-Folder', args.sessionFolder);
      xhr.setRequestHeader('X-Asset-Folder', args.assetFolderName);
      xhr.setRequestHeader('X-Asset-Id', args.assetId);
      // XHR bypasses raw() — Passwort und Sprache von Hand, der Dienst
      // stellt jede Talent-Route hinter das Projekt-Passwort.
      if (sessionPassword) xhr.setRequestHeader('X-Survey-Password', sessionPassword);
      if (sessionLang) xhr.setRequestHeader('X-Survey-Lang', sessionLang);
      if (args.signal) {
        if (args.signal.aborted) {
          xhr.abort();
        } else {
          args.signal.addEventListener('abort', () => xhr.abort(), { once: true });
        }
      }
      xhr.upload.addEventListener('progress', (e) => {
        args.onProgress?.(e.lengthComputable ? e.loaded : 0, args.blob.size);
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const body = xhr.response as { success?: boolean; filename?: string; status?: string } | null;
          if (!body || !body.success) {
            reject(new ApiError(xhr.status, 'Bad upload-file response shape', body));
            return;
          }
          args.onProgress?.(args.blob.size, args.blob.size);
          resolve({ filename: body.filename ?? args.filename, status: body.status ?? 'complete' });
        } else {
          noteUnauthorized(xhr.status, `/upload-file/${args.toolId}`);
          const body = xhr.response as { message?: string } | null;
          reject(new ApiError(xhr.status, body?.message || `${xhr.status} ${xhr.statusText}`, body));
        }
      });
      xhr.addEventListener('error', () => reject(new ApiError(0, `Network error uploading ${args.filename}`)));
      xhr.addEventListener('abort', () => {
        const err = new ApiError(0, `Upload of ${args.filename} aborted`);
        (err as unknown as { name: string }).name = 'AbortError';
        reject(err);
      });
      xhr.send(args.blob);
    });

  const RETRIES = 3;
  const backoffMs = [800, 2000, 4000];
  for (let attempt = 0; ; attempt++) {
    try {
      return await sendOnce();
    } catch (e) {
      if (args.signal?.aborted) throw e;
      if (attempt >= RETRIES || !isRetriableUploadError(e)) throw e;
      const delay = backoffMs[Math.min(attempt, backoffMs.length - 1)];
      await new Promise<void>((r) => {
        const t = setTimeout(r, delay);
        args.signal?.addEventListener('abort', () => { clearTimeout(t); r(); }, { once: true });
      });
      if (args.signal?.aborted) throw e;
    }
  }
}

/** GET /session/:toolId/:sessionFolder — read the per-session KV record and
 *  return the per-asset versions map. The server wraps the payload in a
 *  `{ versions, ...other }` envelope (`GET /session/…` in
 *  `server/routes/talent.ts`); vanilla
 *  unwraps it the same way (see `legacy/index.html:3236-3237`). Falls back
 *  to an empty map if the field is missing. */
export async function fetchVersions(
  toolId: string,
  sessionFolder: string
): Promise<Record<string, UploadVersion[]>> {
  const data = await idempotentJson<{ versions?: Record<string, UploadVersion[]> }>(
    `/session/${toolId}/${encodeURIComponent(sessionFolder)}`,
  );
  return data.versions ?? {};
}

/** PATCH /session/:toolId/:sessionFolder — update a version note.
 *
 *  KV update only requires `{ assetId, version, note }`, but the server also
 *  rewrites the WebDAV note file (`{prefix}_NOTE.txt`) when `assetIdx` +
 *  `assetFolder` are provided (`PATCH /session/…` in `server/routes/talent.ts`). Vanilla always sends the
 *  full payload (legacy/index.html:10162) so the WebDAV note stays in sync —
 *  we mirror that. `note: null` clears the note (KV write null + DELETE on
 *  WebDAV note file). */
export async function patchVersionNote(
  toolId: string,
  sessionFolder: string,
  payload: {
    assetId: string;
    version: number;
    note: string | null;
    assetIdx?: number;
    assetFolder?: string;
    noteFilename?: string;
  }
): Promise<void> {
  await raw(`/session/${toolId}/${encodeURIComponent(sessionFolder)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/** PATCH /session/:toolId/:sessionFolder/final — mark (or clear) the
 *  "final pick" version for an asset.
 *
 *  Wire shape: `{ assetId, assetIdx, version, assetFolder, prevVersion?,
 *  prevAssetFolder? }`. Pass `version: null` to clear the marker (the server
 *  DELETEs the previous 00-FINAL-VERSION file when `prevVersion` is provided).
 *  When `version` is a number the server:
 *    1. PUTs an empty `00-FINAL-VERSION` file into the matching `Version N/`
 *       folder on WebDAV,
 *    2. DELETEs the previous marker file when `prevVersion != version`,
 *    3. clears `isFinal` on every other version of this asset in KV and sets
 *       it on the picked one (single-flag-per-asset invariant).
 *
 *  Returns the updated versions list for `assetId` so the caller can
 *  reconcile its store with server truth. */
export async function setFinalVersion(
  toolId: string,
  sessionFolder: string,
  payload: {
    assetId: string;
    assetIdx: number;
    version: number | null;
    assetFolder: string;
    prevVersion?: number | null;
    prevAssetFolder?: string;
  }
): Promise<{ versions: UploadVersion[] }> {
  const data = await json<{ success: true; versions: UploadVersion[] }>(
    `/session/${toolId}/${encodeURIComponent(sessionFolder)}/final`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return { versions: data.versions };
}

/** GET /stream/... proxies a naked `<audio src>` GET — no custom headers
 *  reach the request, so `raw()`'s automatic X-Survey-Lang header attachment
 *  (see `sessionLang` above) never applies here. Multilingual tools must
 *  therefore carry the language as a `?lang=` query param instead so the
 *  server can rebuild the `{cloudPath}/{LANG}/` path (`effectiveCloudPath()`
 *  in `server/routes/shared.ts`). Standard tools: `sessionLang` is undefined → no
 *  param appended.
 *
 *  `assetIdx` ist die GLOBALE 0-basierte Nummer (`assetGlobalIdx`), dieselbe,
 *  die beim Upload in Ordner- und Dateinamen ging. `assetId` lässt den Dienst
 *  das Asset direkt auflösen (predefined über die Config, Custom über den
 *  beim Upload hinterlegten Titel) statt nur über die Position. */
export function streamAudioUrl(
  toolId: string,
  sessionFolder: string,
  assetIdx: number,
  version: number,
  filename: string,
  assetId?: string,
): string {
  const base = `${BASE}/stream/${toolId}/${sessionFolder}/${assetIdx}/${version}/${encodeURIComponent(filename)}`;
  const params = new URLSearchParams();
  if (assetId) params.set('assetId', assetId);
  if (sessionLang) params.set('lang', sessionLang);
  const q = params.toString();
  return q ? `${base}?${q}` : base;
}

/** Stream a video reference from WebDAV via the server proxy.
 *  `videoPath` is stored URL-encoded in the admin config (see
 *  `admin/cloudPath.ts`), so it's concatenated raw — do NOT double-encode. */
export function streamVideoUrl(toolId: string, videoPath: string): string {
  const base = `${BASE}/stream-video/${toolId}/${videoPath}`;
  // Multilingual: die Media-Session ist an die Sprache gebunden, der Dienst
  // braucht sie also auch hier (wie bei streamAudioUrl). Standard: unverändert.
  return sessionLang ? `${base}?lang=${encodeURIComponent(sessionLang)}` : base;
}

/** POST /media-session/:toolId — holt das HttpOnly-Cookie, an dem die
 *  Stream-Routen (/stream, /stream-video) hängen: `<video>`/`<audio>` können
 *  keinen Passwort-Header senden, Cookies gehen same-origin automatisch mit.
 *  12 h gültig; useToolConfig ruft es nach dem Config-Load, der VideoPlayer
 *  vor jedem Retry. Wirft nie — ohne Session scheitern nur die Streams, der
 *  Rest des Tools bleibt benutzbar. */
export async function ensureMediaSession(toolId: string): Promise<boolean> {
  try {
    await raw(`/media-session/${toolId}`, { method: 'POST' });
    return true;
  } catch {
    return false;
  }
}

/** HEAD auf das Cloud-Video: Größe in Bytes, oder `null` = unbekannt
 *  (Fehlerstatus, Netzfehler, keine Content-Length). Läuft über `fetchVideo`
 *  — bei 401 (Media-Session nach 12 h abgelaufen) einmal Session erneuern und
 *  erneut fragen (Integrations-Review 2026-09-24). Früher lieferte jeder
 *  Fehler 0, und 0 galt als „klein genug": ein großes Video ging ungefragt
 *  als Premix mit. Aufrufer: Upload-Entscheidung (`shouldAutoUploadCloudVideo`,
 *  null = wie > 500 MB) und MP4-Export (Speicher- vs. Streaming-Ausgabe). */
export async function videoInputSize(
  toolId: string,
  videoPath: string
): Promise<number | null> {
  try {
    const res = await fetchVideo(streamVideoUrl(toolId, videoPath), {
      method: 'HEAD',
      refreshMediaSession: () => ensureMediaSession(toolId),
    });
    const len = res.headers.get('content-length');
    const n = len ? parseInt(len, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

/** POST /delete-asset/:toolId — delete an asset's WebDAV folder + clear its
 *  KV version record. Wire shape (`{ sessionFolder, assetIdx, assetId,
 *  assetFolder }`) as in `POST /delete-asset` (`server/routes/talent.ts`) and
 *  vanilla legacy/index.html:3736-3745. */
export async function deleteCustomAsset(
  toolId: string,
  payload: {
    sessionFolder: string;
    assetIdx: number;
    assetId: string;
    assetFolder: string;
  }
): Promise<void> {
  await raw(`/delete-asset/${toolId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/** POST /cancel-upload-version/:toolId — wipe a partially-uploaded version
 *  folder on WebDAV after the user cancels mid-upload. Idempotent: a 404
 *  from WebDAV (folder never created) still returns success.
 *
 *  Seit dem Integrations-Review 2026-09-24 entfernt der Dienst nur halbe
 *  (`pending`) Versionen — eine vollständige nur mit ihrer eigenen
 *  `uploadId`. Sonst 409 (`code: 'version_complete'`), nichts gelöscht. */
export async function cancelUploadVersion(
  toolId: string,
  payload: {
    sessionFolder: string;
    assetFolderName: string;
    version: number;
    assetId: string;
    uploadId?: string;
  }
): Promise<void> {
  await raw(`/cancel-upload-version/${toolId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/** POST /rename-asset/:toolId — MOVE an asset folder on WebDAV
 *  (`server/routes/talent.ts`, vanilla legacy/index.html:3762-3766).
 *  `assetId` lets the server take the SOURCE folder from the version record
 *  instead of `oldName`: `oldName` is built from the asset's current position,
 *  but its uploads stay in the folder they were made in (e.g. a custom asset
 *  that shifted back when the admin inserted a predefined asset). */
export async function renameAsset(
  toolId: string,
  sessionFolder: string,
  oldName: string,
  newName: string,
  assetId?: string
): Promise<void> {
  await raw(`/rename-asset/${toolId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionFolder, oldName, newName, ...(assetId ? { assetId } : {}) }),
  });
}

// --------------------------------------------------------------------------
// Admin
// --------------------------------------------------------------------------

/** `active` optional: beim Anlegen setzt das Panel `true` (der Dienst legt
 *  ohnehin aktiv an); beim PATCH fehlt es, damit ein Edit den Status nie
 *  ändert (Audit H3). */
export interface CreateToolPayload extends Omit<ToolConfig, 'created' | 'active'> {
  active?: boolean;
}

/** Create-tool response: success flag, the freshly minted `tool_id`, and
 *  the saved `tool` object so the client can update its list locally
 *  instead of refetching (vanilla legacy/admin.html:1967-1974 — dort gegen
 *  Cloudflare-KV-Eventual-Consistency; auf SQLite nur ein gesparter Request). */
export interface AdminCreateToolResponse {
  success: boolean;
  tool_id: string;
  tool?: ToolConfig;
}

export async function adminCreateTool(
  payload: CreateToolPayload,
): Promise<AdminCreateToolResponse> {
  const res = await json<AdminCreateToolResponse>('/admin/tool', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (res.tool?.assets) {
    res.tool.assets = applyVoiceIdsMigration(res.tool.assets);
  }
  return res;
}

/** Returns the email of the currently authenticated admin (from the
 *  verified Authentik JWT, `server/auth.ts`). `email: null` in local dev
 *  without `DEV_AUTH_EMAIL_OVERRIDE`. */
export async function adminGetMe(): Promise<{ email: string | null }> {
  return json('/admin/me');
}

// ─── Edit-Lock helpers (admin) ──────────────────────────────────────────────
// Backed by /edit-lock/:toolId on the server. Failure modes are surfaced via
// ApiError — callers discriminate on `err.status` + `err.body.reason`:
//   409 active     → tool is live, edit not allowed
//   409 locked     → fresh foreign lock; body.by carries the holder's adminId
//   409 lock-lost  → save attempted without a valid lock (returned by /admin/tool)
//   410 gone       → heartbeat target lock expired or was taken over

export interface EditLockEntry {
  adminId: string;
  sessionId: string;
  acquiredAt: string;
  renewedAt: string;
}

export interface EditLockAcquireResult {
  lock: EditLockEntry;
  /** Server-side `tool.modifiedAt` snapshot at acquire-time. The frontend
   *  compares this against a fresh fetch on AFK-Continue to detect
   *  concurrent edits and prompt for a reload. Null when the tool was
   *  never modified after lock-feature deployment. */
  modifiedAt: string | null;
}

export async function editLockAcquire(
  toolId: string,
  adminId: string,
  sessionId: string,
): Promise<EditLockAcquireResult> {
  return json(`/edit-lock/${toolId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ adminId, sessionId }),
  });
}

export async function editLockHeartbeat(
  toolId: string,
  sessionId: string,
): Promise<void> {
  await raw(`/edit-lock/${toolId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
}

export async function editLockRelease(
  toolId: string,
  sessionId: string,
): Promise<void> {
  await raw(`/edit-lock/${toolId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
}

/** Best-effort tab-close release via fetch + keepalive — survives page
 *  unload like sendBeacon but supports DELETE (sendBeacon is POST-only).
 *  The server accepts sessionId via the `?sessionId=` query param specifically
 *  so this path can fire-and-forget without negotiating a JSON body shape
 *  during unload. Errors are intentionally swallowed; the 600s server-side
 *  TTL is the fail-safe if this never lands. */
export function editLockReleaseBeacon(
  toolId: string,
  sessionId: string,
): void {
  try {
    fetch(
      BASE + `/edit-lock/${toolId}?sessionId=${encodeURIComponent(sessionId)}`,
      { method: 'DELETE', keepalive: true },
    ).catch(() => { /* unload — nothing to do */ });
  } catch {
    /* fetch unavailable in this context — TTL covers it */
  }
}

/** Update-tool response: same shape as create. Returning the full tool lets
 *  the client update its list locally (see AdminCreateToolResponse doc). */
export interface AdminUpdateToolResponse {
  success: boolean;
  tool_id: string;
  tool?: ToolConfig;
}

export async function adminUpdateTool(
  toolId: string,
  patch: Partial<ToolConfig>,
  /** When set, the server requires a matching live edit-lock and auto-
   *  releases it on success. Throws ApiError(409, body.reason='lock-lost')
   *  if the lock has been forcibly taken or expired. Bare PATCHes (without
   *  this) still work for activate / deactivate toggles outside edit-mode. */
  editSessionId?: string,
): Promise<AdminUpdateToolResponse> {
  const body: Partial<ToolConfig> & { _editSessionId?: string } = { ...patch };
  if (editSessionId) body._editSessionId = editSessionId;
  const res = await json<AdminUpdateToolResponse>(`/admin/tool/${toolId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.tool?.assets) {
    res.tool.assets = applyVoiceIdsMigration(res.tool.assets);
  }
  return res;
}

export async function adminListTools(): Promise<Array<ToolConfig & { tool_id: string }>> {
  const res = await json<{ success: true; tools: Array<ToolConfig & { tool_id: string }> }>(
    '/admin/tools'
  );
  const tools = res.tools ?? [];
  return tools.map((t) =>
    t.assets ? { ...t, assets: applyVoiceIdsMigration(t.assets) } : t,
  );
}

export async function adminDeleteTool(toolId: string): Promise<void> {
  await raw(`/admin/tool/${toolId}`, { method: 'DELETE' });
}

/** GET /admin/tool/:toolId/password — decrypt + return the plaintext survey
 *  password on demand. The password is stored AES-GCM-encrypted in KV and is
 *  never shipped in the tool list, so the admin panel fetches it here for the
 *  reveal (eye icon), "Copy info", and edit-form seeding. Returns null if the
 *  tool has no password. `cache: 'no-store'` mirrors the server's no-store —
 *  a plaintext password must never sit in the browser cache. */
export async function adminRevealPassword(toolId: string, lang?: string): Promise<string | null> {
  const res = await json<{ success: boolean; password: string | null }>(
    `/admin/tool/${toolId}/password${lang ? `?lang=${encodeURIComponent(lang)}` : ''}`,
    { cache: 'no-store' },
  );
  return res.password ?? null;
}

export interface VoiceCheckResult {
  valid: boolean;
  name?: string;
  message?: string;
  /** Region in which the voice was found. EU is probed first; `us` means
   *  the voice lives in the US workspace and TTS/STS calls have to be
   *  routed there (DSGVO-relevant — admin UI surfaces a badge). */
  region?: 'eu' | 'us';
}

export async function adminCheckVoice(voiceId: string): Promise<VoiceCheckResult> {
  const res = await json<{ success: boolean; name?: string; error?: string; region?: 'eu' | 'us' }>(
    `/admin/check-voice/${encodeURIComponent(voiceId)}`
  );
  return {
    valid: res.success === true,
    name: res.name,
    message: res.error,
    region: res.region,
  };
}

export async function getBrandPresets(): Promise<BrandPresets> {
  return json('/admin/brand-presets');
}

export async function saveBrandPresets(presets: BrandPresets): Promise<void> {
  await raw('/admin/brand-presets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(presets),
  });
}
